import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, api } from '../../lib/api';
import { useResource } from '../../lib/useResource';
import { Pill, StateCard } from '../../components/StateCard';
import {
  Empty,
  PLATFORM_LABEL,
  dataRequestStateLabel,
  entitlementStateLabel,
  formatDate,
  linkStateLabel,
  relativeDate,
} from '../../components/site';
import type { ApiEnvelope } from '../../lib/types';
import { AccountLayout, useGameNames } from './shell';
import type { AccountMe } from './shell';

const ACCOUNT_STATUS: Record<string, string> = {
  active: '사용 중',
  pending: '이메일 인증 대기',
  suspended: '이용 제한',
  deletion_requested: '삭제 요청됨',
};

function useAccount<T>(endpoint: string, deps: unknown[] = []) {
  return useResource<T>(
    (signal) => api.account(endpoint, { signal }) as Promise<ApiEnvelope<T>>,
    [endpoint, ...deps],
    { emptyWhen: () => false },
  );
}

/** U005 — account home. */
export function AccountHome() {
  const me = useAccount<AccountMe>('/me');
  const games = useGameNames();
  const data = me.data;

  return (
    <AccountLayout title="내 계정" lead="연결한 게임과 계정 상태">
      <StateCard state={me.state} error={me.error} onRetry={me.reload}>
        {data && (
          <>
            <div className="grid grid--3" style={{ marginBottom: 32 }}>
              <div className="stat">
                <span className="stat__label">연결된 게임</span>
                <strong className="stat__value">{data.linked_games.length}</strong>
              </div>
              <div className="stat">
                <span className="stat__label">읽지 않은 선물함</span>
                <strong className="stat__value">{data.unread_inbox}</strong>
              </div>
              <div className="stat">
                <span className="stat__label">이메일 인증</span>
                <strong className="stat__value">{data.account.email_verified_at ? '완료' : '미완료'}</strong>
              </div>
            </div>

            <section className="card" style={{ marginBottom: 28 }}>
              <h2 className="section__title" style={{ marginBottom: 12 }}>계정</h2>
              <dl className="spec-list">
                <dt>이메일</dt><dd>{data.account.email}</dd>
                <dt>상태</dt><dd><Pill tone={data.account.status === 'active' ? 'ok' : 'warn'}>{ACCOUNT_STATUS[data.account.status] ?? data.account.status}</Pill></dd>
                <dt>언어</dt><dd>{{ ko: '한국어', en: 'English' }[data.account.locale] ?? data.account.locale}</dd>
                <dt>시간대</dt><dd>{data.account.timezone ?? '기기 설정 사용'}</dd>
                <dt>2단계 인증</dt><dd>{data.account.mfa_enabled ? '사용 중' : '사용 안 함'}</dd>
              </dl>
            </section>

            <section>
              <h2 className="section__title" style={{ marginBottom: 12 }}>연결된 게임</h2>
              {data.linked_games.length === 0 ? (
                <Empty
                  title="연결된 게임 없음"
                  body="게임에서 받은 연결 코드 입력"
                  action={<Link className="btn btn--primary" to="/account/link">연결 코드 입력</Link>}
                />
              ) : (
                <div className="row-list">
                  {data.linked_games.map((link) => {
                    const slug = games.slug(link.game_id);
                    const body = (
                      <>
                        <div className="row__main">
                          <h3 className="row__title">{games.name(link.game_id)}</h3>
                          <p className="row__excerpt">마지막 접속 {relativeDate(link.last_seen_at)}</p>
                        </div>
                        <div className="row__aside">
                          <Pill tone="neutral">{PLATFORM_LABEL[link.platform] ?? link.platform}</Pill>
                        </div>
                      </>
                    );
                    return slug
                      ? <Link key={link.principal_id} to={`/games/${slug}`} className="row">{body}</Link>
                      : <div key={link.principal_id} className="row">{body}</div>;
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </StateCard>
    </AccountLayout>
  );
}

interface ConnectionsPayload {
  connections: { id: string; game_principal_id: string; state: string; linked_at: string | null; revoked_at: string | null }[];
  note: string;
}

/** U006 — platform and game connections. */
export function AccountConnections() {
  const connections = useAccount<ConnectionsPayload>('/connections');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function unlink(id: string) {
    setBusy(id);
    setError(null);
    try {
      await api.account(`/connections/${id}`, { method: 'DELETE' });
      connections.reload();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '연결 해제 실패');
    } finally {
      setBusy(null);
    }
  }

  const data = connections.data;

  return (
    <AccountLayout title="게임 연결" lead="연결 후에도 저장 데이터·지갑은 각각 유지">
      <StateCard state={connections.state} error={connections.error} onRetry={connections.reload}>
        {data && (
          <>
            {error && <div className="banner" style={{ marginBottom: 20 }}><span aria-hidden="true">!</span><div>{error}</div></div>}

            {data.connections.length === 0 ? (
              <Empty
                title="연결된 항목 없음"
                body="게임에서 받은 연결 코드 입력"
                action={<Link className="btn btn--primary" to="/account/link">연결 코드 입력</Link>}
              />
            ) : (
              <div className="row-list">
                {data.connections.map((connection) => (
                  <div key={connection.id} className="row">
                    <div className="row__main">
                      <h3 className="row__title">{connection.game_principal_id}</h3>
                      <p className="row__excerpt">
                        {connection.state === 'linked'
                          ? `연결 ${formatDate(connection.linked_at)}`
                          : `해제 ${formatDate(connection.revoked_at)}`}
                      </p>
                    </div>
                    <div className="row__aside">
                      <Pill tone={connection.state === 'linked' ? 'ok' : 'neutral'}>{linkStateLabel(connection.state)}</Pill>
                      {connection.state === 'linked' && (
                        <button type="button" className="btn btn--small" disabled={busy === connection.id}
                          onClick={() => unlink(connection.id)}>
                          {busy === connection.id ? '해제 중…' : '연결 해제'}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="banner banner--info" style={{ marginTop: 24 }}>
              <span aria-hidden="true">ℹ</span>
              <div>{data.note}</div>
            </div>

            <div style={{ marginTop: 20 }}>
              <Link className="btn" to="/account/link">새 연결 코드 입력</Link>
            </div>
          </>
        )}
      </StateCard>
    </AccountLayout>
  );
}

/** U010 — the account side of a two-sided link. */
export function AccountLink() {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await api.account('/link-intents/confirm', {
        method: 'POST',
        body: { code },
      }) as ApiEnvelope<{ note: string }>;
      setDone(response.data.note);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '연결 실패');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AccountLayout title="게임 연결" lead="게임 코드 입력 후 양쪽에서 연결 확인">
      {done ? (
        <div className="card">
          <p style={{ margin: '0 0 16px' }}>{done}</p>
          <Link className="btn btn--primary" to="/account/connections">연결 목록 보기</Link>
        </div>
      ) : (
        <form className="form-card" onSubmit={submit}>
          {error && <div className="banner" style={{ marginBottom: 16 }}><span aria-hidden="true">!</span><div>{error}</div></div>}
          <label htmlFor="link-code">연결 코드</label>
          <input id="link-code" required value={code} autoComplete="one-time-code"
            onChange={(event) => setCode(event.target.value.toUpperCase())} />
          <p className="form-foot">코드 만료 시 게임에서 새 코드 받기</p>
          <button type="submit" className="btn btn--primary" disabled={busy || !code}>{busy ? '확인 중…' : '연결 확인'}</button>
        </form>
      )}
    </AccountLayout>
  );
}

interface SecurityPayload {
  mfa_enabled: boolean;
  password_set: boolean;
  sessions: { current: boolean; started_at: string }[];
  last_recovery_at: string | null;
}

/** U007 — account security. */
export function AccountSecurity() {
  const security = useAccount<SecurityPayload>('/security');
  const data = security.data;

  return (
    <AccountLayout title="보안" lead="로그인 수단과 현재 세션">
      <StateCard state={security.state} error={security.error} onRetry={security.reload}>
        {data && (
          <>
            <section className="card" style={{ marginBottom: 24 }}>
              <dl className="spec-list">
                <dt>비밀번호</dt><dd>{data.password_set ? '설정됨' : '미설정'}</dd>
                <dt>2단계 인증</dt>
                <dd><Pill tone={data.mfa_enabled ? 'ok' : 'neutral'}>{data.mfa_enabled ? '사용 중' : '사용 안 함'}</Pill></dd>
                <dt>마지막 복구</dt><dd>{data.last_recovery_at ? formatDate(data.last_recovery_at, true) : '기록 없음'}</dd>
              </dl>
            </section>

            <section className="card" style={{ marginBottom: 24 }}>
              <h2 className="section__title" style={{ marginBottom: 12 }}>세션</h2>
              {data.sessions.map((session, index) => (
                <div key={index} className="status-row">
                  <span className="status-row__name">{session.current ? '현재 기기' : '다른 기기'}</span>
                  <span className="status-row__meta">{formatDate(session.started_at, true)}</span>
                  {session.current && <Pill tone="ok">사용 중</Pill>}
                </div>
              ))}
              <p style={{ margin: '12px 0 0', fontSize: 13, color: 'var(--text-tertiary)' }}>
                위치 정보 미수집 · 접속 지역 표시 없음
              </p>
            </section>

            <div className="banner banner--info">
              <span aria-hidden="true">ℹ</span>
              <div>
                비밀번호 변경은 <Link to="/account/recovery">비밀번호 찾기</Link>에서 가능. 변경 시 다른 기기 로그인도 해제 가능
              </div>
            </div>
          </>
        )}
      </StateCard>
    </AccountLayout>
  );
}

interface PreferencesPayload {
  locale: string;
  timezone: string | null;
  consents: { purpose: string; granted: boolean; recorded_at: string | null; required: boolean }[];
}

const PURPOSE_LABEL: Record<string, string> = {
  terms: '이용약관 동의',
  service: '서비스 안내 수신',
  marketing: '마케팅 정보 수신',
  analytics: '이용 분석',
};

/** U008 — preferences and consent. Required purposes cannot be switched off here. */
export function AccountPreferences() {
  const preferences = useAccount<PreferencesPayload>('/preferences');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const data = preferences.data;

  async function save(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    setSaved(false);

    const consents: { purpose: string; granted: boolean }[] = [];
    for (const consent of data?.consents ?? []) {
      if (consent.required) continue;
      consents.push({ purpose: consent.purpose, granted: form.get(`consent:${consent.purpose}`) === 'on' });
    }

    try {
      await api.account('/preferences', {
        method: 'PATCH',
        body: { locale: form.get('locale'), timezone: form.get('timezone') || null, consents },
      });
      setSaved(true);
      preferences.reload();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '저장 실패');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AccountLayout title="설정" lead="언어·시간대와 선택 동의">
      <StateCard state={preferences.state} error={preferences.error} onRetry={preferences.reload}>
        {data && (
          <form className="form-card" onSubmit={save}>
            {error && <div className="banner" style={{ marginBottom: 16 }}><span aria-hidden="true">!</span><div>{error}</div></div>}
            {saved && <div className="banner banner--info" style={{ marginBottom: 16 }}><span aria-hidden="true">ℹ</span><div>저장 완료</div></div>}

            <div className="form-grid">
              <div>
                <label htmlFor="pref-locale">언어</label>
                <select id="pref-locale" name="locale" defaultValue={data.locale}>
                  <option value="ko">한국어</option>
                  <option value="en">English</option>
                </select>
              </div>
              <div>
                <label htmlFor="pref-tz">시간대</label>
                <input id="pref-tz" name="timezone" defaultValue={data.timezone ?? ''} placeholder="Asia/Seoul" />
              </div>
            </div>

            <h2 className="section__title" style={{ margin: '24px 0 12px' }}>동의 항목</h2>
            {data.consents.length === 0 && (
              <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--text-tertiary)' }}>
                기록된 동의 항목 없음 · 게임에서 선택한 동의 내역 확인 가능
              </p>
            )}
            {data.consents.map((consent) => (
              <label key={consent.purpose} className="form-check">
                <input
                  type="checkbox"
                  name={`consent:${consent.purpose}`}
                  defaultChecked={consent.granted}
                  disabled={consent.required}
                />
                <span>
                  {PURPOSE_LABEL[consent.purpose] ?? consent.purpose}
                  {consent.required && <span style={{ color: 'var(--text-tertiary)' }}> · 필수 항목</span>}
                  {consent.recorded_at && (
                    <><br /><span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>기록 {formatDate(consent.recorded_at)}</span></>
                  )}
                </span>
              </label>
            ))}

            <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? '저장 중…' : '저장'}</button>
          </form>
        )}
      </StateCard>
    </AccountLayout>
  );
}

interface DataRequestsPayload {
  requests: { reference: string; kind: string; state: string; retention_holds: { reason: string }[]; completed_at: string | null }[];
}

/** U009 — export and deletion. Deletion needs the password again. */
export function AccountData() {
  const requests = useAccount<DataRequestsPayload>('/data-requests');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const data = requests.data;

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await api.account('/data-requests', {
        method: 'POST',
        body: {
          kind: form.get('kind'),
          password: form.get('password'),
          all_games: form.get('all_games') === 'on',
        },
      });
      (event.target as HTMLFormElement).reset();
      requests.reload();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '요청 실패');
    } finally {
      setBusy(false);
    }
  }

  async function cancel(reference: string) {
    setError(null);
    try {
      await api.account(`/data-requests/${reference}/cancel`, { method: 'POST' });
      requests.reload();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : '취소 실패');
    }
  }

  return (
    <AccountLayout title="데이터·탈퇴" lead="보관 중인 데이터 내보내기 또는 삭제 요청">
      <StateCard state={requests.state} error={requests.error} onRetry={requests.reload}>
        {data && (
          <>
            {error && <div className="banner" style={{ marginBottom: 20 }}><span aria-hidden="true">!</span><div>{error}</div></div>}

            <form className="form-card" onSubmit={submit} style={{ marginBottom: 32 }}>
              <label htmlFor="data-kind">요청 종류</label>
              <select id="data-kind" name="kind" defaultValue="export">
                <option value="export">내보내기</option>
                <option value="delete">삭제(탈퇴)</option>
              </select>

              <label className="form-check">
                <input type="checkbox" name="all_games" defaultChecked />
                <span>연결된 모든 게임에 대해 요청</span>
              </label>

              <label htmlFor="data-password">비밀번호 확인</label>
              <input id="data-password" name="password" type="password" autoComplete="current-password" required />

              <div className="banner banner--info">
                <span aria-hidden="true">ℹ</span>
                <div>결제 기록 등 법정 보존 항목은 보존 기간까지 유지 · 게임 내 잔액 환불 불가</div>
              </div>

              <button type="submit" className="btn btn--primary" disabled={busy}>{busy ? '접수 중…' : '요청 접수'}</button>
            </form>

            <h2 className="section__title" style={{ marginBottom: 12 }}>요청 내역</h2>
            {data.requests.length === 0 ? (
              <Empty title="접수한 요청 없음" />
            ) : (
              <div className="row-list">
                {data.requests.map((request) => (
                  <div key={request.reference} className="row">
                    <div className="row__main">
                      <h3 className="row__title">{request.kind === 'delete' ? '삭제' : '내보내기'} · {request.reference}</h3>
                      <p className="row__excerpt">
                        {request.retention_holds.length > 0
                          ? `보존 의무: ${request.retention_holds.map((hold) => hold.reason).join(', ')}`
                          : '보존 의무 대상 없음'}
                      </p>
                    </div>
                    <div className="row__aside">
                      <Pill tone={request.state === 'COMPLETED' ? 'ok' : 'info'}>{dataRequestStateLabel(request.state)}</Pill>
                      <span>{request.completed_at ? formatDate(request.completed_at) : '처리 중'}</span>
                      {!['PROCESSING', 'COMPLETED', 'PARTIALLY_COMPLETED', 'CANCELLED'].includes(request.state) && (
                        <button type="button" className="btn btn--small" onClick={() => cancel(request.reference)}>취소</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <p style={{ marginTop: 20, fontSize: 13, color: 'var(--text-tertiary)' }}>
              계정에 연결하지 않은 플레이 데이터는 별도 관리. 관련 요청은{' '}
              <Link to="/privacy/requests">내 데이터 요청</Link>에서 접수
            </p>
          </>
        )}
      </StateCard>
    </AccountLayout>
  );
}

interface InboxPayload {
  messages_by_game: {
    game_id: string;
    messages: { id: string; title: string; read: boolean; claim_state: string; expires_at: string | null }[];
  }[];
  note: string;
}

const CLAIM_TONE: Record<string, 'ok' | 'warn' | 'info' | 'neutral'> = {
  GRANTED: 'info',
  CLAIMED: 'ok',
  EXPIRED: 'warn',
  NONE: 'neutral',
};

/** U011 — inbox. Per-game, never summed across games. */
export function AccountInbox() {
  const inbox = useAccount<InboxPayload>('/inbox');
  const games = useGameNames();
  const data = inbox.data;

  return (
    <AccountLayout title="선물함" lead="게임별 받은 보상 · 수령은 각 게임에서 가능">
      <StateCard state={inbox.state} error={inbox.error} onRetry={inbox.reload}>
        {data && (
          <>
            {data.messages_by_game.length === 0 ? (
              <Empty title="받은 선물 없음" body="이벤트·공지 선물 수신 후 목록에서 확인 가능" />
            ) : (
              data.messages_by_game.map((group) => (
                <section key={group.game_id} style={{ marginBottom: 32 }}>
                  <h2 className="section__title" style={{ marginBottom: 12 }}>{games.name(group.game_id)}</h2>
                  <div className="row-list">
                    {group.messages.map((message) => (
                      <div key={message.id} className="row">
                        <div className="row__main">
                          <h3 className="row__title">
                            {!message.read && <span style={{ color: 'var(--ak-orange-600)', marginRight: 6 }}>새 항목</span>}
                            {message.title}
                          </h3>
                          <p className="row__excerpt">
                            {message.expires_at ? `${formatDate(message.expires_at, true)}까지 수령` : '수령 기한 없음'}
                          </p>
                        </div>
                        <div className="row__aside">
                          <Pill tone={CLAIM_TONE[message.claim_state] ?? 'neutral'}>{entitlementStateLabel(message.claim_state)}</Pill>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              ))
            )}

            <div className="banner banner--info">
              <span aria-hidden="true">ℹ</span>
              <div>{data.note}</div>
            </div>
          </>
        )}
      </StateCard>
    </AccountLayout>
  );
}

interface EntitlementsPayload {
  entitlements: {
    game_id: string; entitlement_code: string; kind: string; state: string;
    expires_at: string | null; recovery_path: string;
  }[];
  note: string;
}

/** U012 — purchase and reward entitlements. */
export function AccountEntitlements() {
  const entitlements = useAccount<EntitlementsPayload>('/entitlements');
  const games = useGameNames();
  const data = entitlements.data;

  return (
    <AccountLayout title="구매·보상" lead="게임에서 확인된 권리만 표시">
      <StateCard state={entitlements.state} error={entitlements.error} onRetry={entitlements.reload}>
        {data && (
          <>
            {data.entitlements.length === 0 ? (
              <Empty title="확인된 권리 없음" body="구매·보상으로 받은 권리 확인 가능" />
            ) : (
              <div className="row-list">
                {data.entitlements.map((entitlement, index) => (
                  <div key={`${entitlement.game_id}:${entitlement.entitlement_code}:${index}`} className="row">
                    <div className="row__main">
                      <h3 className="row__title">{entitlement.entitlement_code}</h3>
                      <p className="row__excerpt">
                        {games.name(entitlement.game_id)} · {entitlement.kind}
                        {entitlement.expires_at ? ` · ${formatDate(entitlement.expires_at)}까지` : ''}
                      </p>
                    </div>
                    <div className="row__aside">
                      <Pill tone={entitlement.state === 'ACTIVE' ? 'ok' : 'neutral'}>{entitlementStateLabel(entitlement.state)}</Pill>
                      <span>복구: 게임 내 구매 복원</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="banner banner--info" style={{ marginTop: 24 }}>
              <span aria-hidden="true">ℹ</span>
              <div>{data.note}</div>
            </div>
          </>
        )}
      </StateCard>
    </AccountLayout>
  );
}
